/**
 * One colour in, a light/dark pair out - for a person choosing their own
 * feature colour (the Argus colour first; `todos/argus.md`).
 *
 * Someone picks any colour. This keeps its hue and saturation and finds two
 * shades of it: the lightest that still reads as text on the light page, and
 * the darkest that reads on a dark panel - so the choice cannot produce a
 * colour nobody can read. `clashesOf` names the colours it sits too close to
 * that already mean something in the app (Critical red, Watch yellow, the
 * green of a switch that is on, the app's blue), so the picker can warn
 * rather than refuse.
 *
 * Pure: no DOM, no storage. The numbers are WCAG 2 contrast ratios.
 */

export interface ColourPair {
  /** For light themes: readable on the light page. */
  light: string;
  /** For dark themes: readable on a dark panel. */
  dark: string;
}

/** The grounds a feature colour is read against, per theme. */
export const LIGHT_GROUND = '#f3f5f8';
export const DARK_GROUND = '#212328';

/** Text needs 4.5:1; a little over leaves room for anti-aliasing and tints. */
const LIGHT_TARGET = 4.8;
const DARK_TARGET = 6;

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  const n = parseInt(hex.replace('#', '').slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]: Rgb): string {
  const part = (v: number): string =>
    Math.round(Math.max(0, Math.min(255, v)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

function luminance(hex: string): number {
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio between two colours, 1 to 21. */
export function contrast(a: string, b: string): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** Hue in degrees, saturation and lightness 0-1. */
export function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255) as Rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  return [h, s, l];
}

export function fromHsl(h: number, s: number, l: number): string {
  const f = (n: number): number => {
    const k = (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return rgbToHex([f(0) * 255, f(8) * 255, f(4) * 255]);
}

/** The readable pair for a chosen colour. Hue kept; only lightness moves. */
export function colourPair(base: string): ColourPair {
  const [h, s] = toHsl(base);
  let light = fromHsl(h, s, 0.1);
  for (let l = 0.7; l >= 0.1; l -= 0.005) {
    const candidate = fromHsl(h, s, l);
    if (contrast(candidate, LIGHT_GROUND) >= LIGHT_TARGET) {
      light = candidate;
      break;
    }
  }
  const darkS = Math.min(1, s * 0.95);
  let dark = fromHsl(h, darkS, 0.92);
  for (let l = 0.3; l <= 0.92; l += 0.005) {
    const candidate = fromHsl(h, darkS, l);
    if (contrast(candidate, DARK_GROUND) >= DARK_TARGET) {
      dark = candidate;
      break;
    }
  }
  return { light, dark };
}

/** Hues that already mean something in the app, and how near is too near. */
const MEANINGS: ReadonlyArray<{ hue: number; width: number; label: string }> = [
  { hue: 4, width: 10, label: 'close to Critical red' },
  { hue: 45, width: 12, label: 'close to Watch yellow' },
  { hue: 150, width: 18, label: 'close to the green of a switch that is on' },
  { hue: 222, width: 16, label: "close to the app's blue" },
];

/** What a chosen colour could be mistaken for. Empty means clear. */
export function clashesOf(base: string): string[] {
  const [h, s] = toHsl(base);
  if (s < 0.18) return ['too grey to read as a colour of its own'];
  return MEANINGS.filter((m) => {
    const distance = Math.abs((((h - m.hue) % 360) + 540) % 360 - 180);
    return distance <= m.width;
  }).map((m) => m.label);
}
